import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { PageTransition } from '../components/PageTransition';
import ReadingProgress from '../components/ReadingProgress';
import Seo from '../components/Seo';
import SocialShare from '../components/SocialShare';
import { requestPosts } from '../actions/post';
import { transform } from '../services/post';
import { usePrism, highlightCode } from '../utils/prism';
import {
  calculateReadingTime,
  formatPostDate,
  postCategories,
  postExcerpt,
  postSlug,
  postTitle,
  sortPostsByDate,
} from '../utils/postHelpers';
import { REACT_APP_OWNER, REACT_APP_SITE_URL } from '../env';

const parserOptions = { decodeEntities: true, transform };

function Post({ posts, fetchPosts }) {
  const { slug } = useParams();
  usePrism();

  useEffect(() => {
    if (!posts || posts.length === 0) fetchPosts();
  }, [fetchPosts, posts]);

  const ordered = useMemo(() => sortPostsByDate(posts || []), [posts]);
  const index = ordered.findIndex((item) => postSlug(item) === slug);
  const post = index >= 0 ? ordered[index] : null;

  // Newer posts sort first, so the previous post in reading order sits after this one.
  const newer = index > 0 ? ordered[index - 1] : null;
  const older = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null;

  useEffect(() => {
    if (post) highlightCode();
  }, [post]);

  if (!post) {
    const stillLoading = !posts || posts.length === 0;

    return (
      <div className="shell py-24">
        <p className="label">{stillLoading ? 'Loading' : 'Not found'}</p>
        <h1 className="mt-5 text-title">
          {stillLoading ? 'Opening the post' : 'That post is not here'}
        </h1>
        {!stillLoading && (
          <p className="mt-5 max-w-prose text-deck text-muted-foreground">
            The link may be out of date. The full index is one step away.
          </p>
        )}
        <Link to="/posts" className="link-rule mt-8 inline-block text-sm">
          Back to all writing
        </Link>
      </div>
    );
  }

  const title = postTitle(post);
  const siteUrl = REACT_APP_SITE_URL || 'https://dennisnyabuti.com';
  const url = `${siteUrl}/posts/${postSlug(post)}`;
  const description = postExcerpt(post, 180);
  const categories = postCategories(post);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    datePublished: post.date,
    dateModified: post.modified || post.date,
    author: { '@type': 'Person', name: REACT_APP_OWNER || 'Dennis M. Nyabuti', url: siteUrl },
    mainEntityOfPage: url,
  };

  return (
    <>
      <Seo
        title={`${title} | Dennis M. Nyabuti`}
        description={description}
        path={`/posts/${postSlug(post)}`}
        type="article"
        schema={articleSchema}
      />
      <ReadingProgress />

      <PageTransition>
        <article className="shell pb-8 pt-12 sm:pt-16">
          <Link to="/posts" className="label inline-flex items-center gap-2 hover:text-signal transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            All writing
          </Link>

          <header className="mt-8 border-b border-rule pb-8">
            <h1 className="max-w-4xl text-title">{title}</h1>
            <div className="label mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
              <span>{formatPostDate(post.date)}</span>
              <span aria-hidden="true" className="text-rule">/</span>
              <span>{calculateReadingTime(post.content)} min read</span>
              {categories.length > 0 && (
                <>
                  <span aria-hidden="true" className="text-rule">/</span>
                  <span className="text-signal">{categories.join(', ')}</span>
                </>
              )}
            </div>
          </header>

          <div className="prose-editorial prose max-w-prose py-12 line-numbers">
            {ReactHtmlParser(post.content, parserOptions)}
          </div>

          <div className="max-w-prose border-t border-rule py-8">
            <SocialShare url={url} title={title} description={description} />
          </div>

          {(newer || older) && (
            <nav className="grid gap-px border-t border-rule sm:grid-cols-2" aria-label="More posts">
              {older ? (
                <Link to={`/posts/${postSlug(older)}`} className="group py-8 pr-6">
                  <span className="label inline-flex items-center gap-2 group-hover:text-signal transition-colors">
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    Older
                  </span>
                  <span className="mt-3 block font-display text-xl leading-snug group-hover:text-signal transition-colors">
                    {postTitle(older)}
                  </span>
                </Link>
              ) : (
                <span />
              )}

              {newer && (
                <Link
                  to={`/posts/${postSlug(newer)}`}
                  className="group border-t border-rule py-8 sm:border-l sm:border-t-0 sm:pl-8 sm:text-right"
                >
                  <span className="label inline-flex items-center gap-2 group-hover:text-signal transition-colors">
                    Newer
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span className="mt-3 block font-display text-xl leading-snug group-hover:text-signal transition-colors">
                    {postTitle(newer)}
                  </span>
                </Link>
              )}
            </nav>
          )}
        </article>
      </PageTransition>
    </>
  );
}

Post.propTypes = {
  posts: PropTypes.array,
  fetchPosts: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({ posts: state.posts }),
  (dispatch) => ({ fetchPosts: () => dispatch(requestPosts()) }),
)(Post);
